"use client";
import { ChangeEvent, useEffect, useState } from "react";
import {
  HydratedChatbot,
  getMessageFromNetworkError,
  useRenameChatbot,
} from "@zapier/client";

import {
  Button,
  Field,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Text,
  TextInput,
} from "@zapier/design-system";

import { useNotification } from "@zapier/utils";
import {
  useAutoSelectInputText,
  useEmitChatbotRenameModalEvent,
} from "@zapier/shared";
import { useTranslations } from "@zapier/i18n";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  chatbot: HydratedChatbot | undefined;
  folderId?: string;
}

export function ChatbotRenameModal({
  isOpen,
  onClose,
  chatbot,
  folderId,
}: Props) {
  const nameInputIdentifier = "rename-chatbot-name";
  const [name, setName] = useState(chatbot?.name ?? "");
  const { createNotification } = useNotification();
  const { rename, isLoading } = useRenameChatbot({ folderId });
  const { emit } = useEmitChatbotRenameModalEvent();
  const t = useTranslations("chatbot-rename-modal");

  useEffect(() => {
    if (!isOpen) return;
    setName(chatbot?.name ?? "");
  }, [chatbot?.name, isOpen]);

  useAutoSelectInputText({
    isOpen,
    inputSelector: `#${nameInputIdentifier}`,
    value: chatbot?.name,
  });

  const handleCancel = () => {
    emit("cancel");
    onClose();
  };

  const handleSubmit = () => {
    if (!chatbot?.id) {
      return;
    }

    const trimmedName = name.trim();

    if (!trimmedName || trimmedName === chatbot.name) {
      handleCancel();
      return;
    }

    rename(
      { id: chatbot.id, name: trimmedName },
      {
        onSuccess: () => {
          createNotification({
            message: (
              <Text>
                {t("notifications.rename-success", {
                  oldName: chatbot.name ?? "",
                  newName: trimmedName,
                })}
              </Text>
            ),
            type: "success",
          });
        },
        onError: (error) => {
          createNotification({
            title: t("notifications.rename-error", {
              chatbotName: chatbot.name ?? "",
            }),
            message: getMessageFromNetworkError(error),
            type: "error",
          });
        },
      },
    );

    emit("confirm");
    onClose();
  };

  if (!isOpen || !chatbot) {
    return;
  }

  const trimmedName = name.trim();
  const isSubmitDisabled =
    !trimmedName || trimmedName === chatbot.name || isLoading;

  return (
    <ModalDialogRoot
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleCancel();
        }
      }}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          handleSubmit();
        }}
      >
        <ModalDialogHeader textAlign="start">
          {t("header.rename-title", { chatbotName: chatbot.name ?? "" })}
        </ModalDialogHeader>

        <ModalDialogBody textAlign="start">
          <Field
            label={t("form.name-label")}
            inputId={nameInputIdentifier}
            isRequired
            renderInput={(inputProps) => (
              <TextInput
                {...inputProps}
                // eslint-disable-next-line jsx-a11y/no-autofocus -- intentionally focus the input when the modal opens
                autoFocus
                id={nameInputIdentifier}
                value={name}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setName(event.target.value)
                }
                placeholder={t("form.name-placeholder")}
                isFullWidth
              />
            )}
          />
        </ModalDialogBody>

        <ModalDialogFooter>
          <Button
            variant="secondary"
            type="button"
            onPress={handleCancel}
            isDisabled={isLoading}
          >
            {t("buttons.cancel")}
          </Button>
          <Button type="submit" isDisabled={isSubmitDisabled}>
            {t("buttons.save")}
          </Button>
        </ModalDialogFooter>
      </form>
    </ModalDialogRoot>
  );
}
